import React, { useState, useEffect } from "react";
import { Download, FileDown } from "lucide-react";
import purchaseService from "../../services/purchaseService";
import { useSelector } from "react-redux";
const PurchaseHistory = () => {
  const [purchaseData, setPurchaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { token } = useSelector((state) => state.auth);

  useEffect(() => {
    const fetchPurchaseHistory = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await purchaseService.getPurchaseHistory(token);
        setPurchaseData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPurchaseHistory();
  }, [token]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "0";
    return Number(num).toLocaleString();
  };

  const formatCurrency = (amount, currency = "usd") => {
    if (amount === null || amount === undefined) return "N/A";

    const currencyCode = (currency || "usd").toUpperCase();

    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currencyCode,
        maximumFractionDigits: 2,
      }).format(Number(amount));
    } catch (error) {
      const currencySymbols = {
        USD: "$",
        NGN: "₦",
        EUR: "€",
        GBP: "£",
      };
      const symbol = currencySymbols[currencyCode] || "";
      return `${symbol}${Number(amount).toLocaleString()}`;
    }
  };

  const exportToCSV = () => {
    if (!purchaseData || !purchaseData.purchaseHistory) {
      setError("No data available to export");
      return;
    }

    // CSV headers
    const headers = ['Plan', 'Date', 'Amount', 'Tokens', 'Status'];

    // Convert purchase data to CSV format
    const csvData = purchaseData.purchaseHistory.map(purchase => [
      purchase.plan,
      formatDate(purchase.date),
      formatCurrency(
        purchase.amount,
        purchase.currency || purchaseData.summary?.currency || "usd"
      ),
      purchase.tokens,
      purchase.paymentStatus
        ? purchase.paymentStatus.charAt(0).toUpperCase() + purchase.paymentStatus.slice(1)
        : "Completed"
    ]);

    // Combine headers and data
    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `purchase-history-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadReceipt = async (purchaseId) => {
    try {
      
      const blob = await purchaseService.downloadReceipt(purchaseId, token);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `receipt-${purchaseId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      setError(`Failed to download receipt: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Purchase History</h2>
          <button
            disabled
            className="bg-gray-300 text-gray-500 px-4 py-2 rounded-lg font-medium cursor-not-allowed flex items-center gap-2"
          >
            <FileDown className="w-4 h-4" />
            Export CSV
          </button>
        </div>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Purchase History</h2>
          <button
            disabled
            className="bg-gray-300 text-gray-500 px-4 py-2 rounded-lg font-medium cursor-not-allowed flex items-center gap-2"
          >
            <FileDown className="w-4 h-4" />
            Export CSV
          </button>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">Error: {error}</p>
        </div>
      </div>
    );
  }

  if (!purchaseData || !purchaseData.purchaseHistory) {
    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Purchase History</h2>
          <button
            disabled
            className="bg-gray-300 text-gray-500 px-4 py-2 rounded-lg font-medium cursor-not-allowed flex items-center gap-2"
          >
            <FileDown className="w-4 h-4" />
            Export CSV
          </button>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-gray-600">No purchase history found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">

      {/* Summary Cards */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h3 className="text-sm font-medium text-gray-500">Total Purchases</h3>
          <p className="text-2xl font-bold text-gray-900">{purchaseData.pagination.totalPurchases}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h3 className="text-sm font-medium text-gray-500">Total Tokens</h3>
          <p className="text-2xl font-bold text-gray-900">{formatNumber(purchaseData.summary.totalTokensPurchased)}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h3 className="text-sm font-medium text-gray-500">Total Spent</h3>
          <p className="text-2xl font-bold text-gray-900">{purchaseData.summary.totalSpent}</p>
        </div>
      </div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Purchase History</h2>
        <button
          onClick={exportToCSV}
          className="bg-[#075038] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#075038]/90 transition-colors flex items-center gap-2"
        >
          <FileDown className="w-4 h-4" />
          Export CSV
        </button>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tokens
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {purchaseData.purchaseHistory.map((purchase) => (
                <tr key={purchase._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {purchase.plan}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(purchase.date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                    {formatCurrency(
                      purchase.amount,
                      purchase.currency || purchaseData.summary.currency || "usd"
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                    {formatNumber(purchase.tokens)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                      {purchase.paymentStatus.charAt(0).toUpperCase() + purchase.paymentStatus.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleDownloadReceipt(purchase._id)}
                        className="text-gray-600 hover:text-gray-900"
                        title="Download Receipt"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>


    </div>
  );
};

export default PurchaseHistory;
